import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, User, Package, Ruler, 
  Wrench, Square, Truck, Check, Plus, Trash2,
  AlertTriangle, Calculator, Percent, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "/logo.png";

const steps = [
  { id: 1, title: 'Client', icon: User },
  { id: 2, title: 'Matière', icon: Package },
  { id: 3, title: 'Dimensions', icon: Ruler },
  { id: 4, title: 'Façonnage', icon: Wrench },
  { id: 5, title: 'Chants', icon: Square },
  { id: 6, title: 'Services', icon: Truck },
  { id: 7, title: 'TVA & Remise', icon: Percent },
];

const planDesignations = [
  "Plan de travail",
  "Crédence",
  "Îlot central",
  "Table",
  "Retombée",
  "Jambage",
  "Autre"
];

const tvaOptions = [
  { value: 20, label: "TVA 20%" },
  { value: 10, label: "TVA 10%" },
  { value: 0, label: "H.T (sans TVA)" },
];

export default function Configurator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const duplicateFromId = searchParams.get('duplicate');
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [finishings, setFinishings] = useState([]);
  const [edges, setEdges] = useState([]);
  const [serviceSettings, setServiceSettings] = useState({});
  const [installationRules, setInstallationRules] = useState(null);

  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);

  // Form State
  const [clientInfo, setClientInfo] = useState({
    name: '', phone: '', email: '', address: '', city: '', postal_code: '',
    has_floor: false, floor_number: 0, has_elevator: false
  });
  // Material selection in 3 steps
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedThickness, setSelectedThickness] = useState('');
  const [plans, setPlans] = useState([{ id: 1, designation: 'Plan de travail', length_mm: 0, width_mm: 0 }]);
  const [selectedFinishings, setSelectedFinishings] = useState([]);
  const [selectedEdges, setSelectedEdges] = useState([]);
  // Simplified services - single package
  const [services, setServices] = useState({
    installation_package: false,
    delivery_address: '',
    delivery_city: '',
    delivery_postal_code: ''
  });
  const [installationPrice, setInstallationPrice] = useState(null);
  // Manual install forfait override (available for Admin AND Pro — when chantier address is unknown)
  const [installationAddressUnknown, setInstallationAddressUnknown] = useState(false);
  const [manualForfaitChoice, setManualForfaitChoice] = useState(950); // 950 or 1100
  // TVA + Discount at the end
  const [tvaRate, setTvaRate] = useState(20);
  const [discountType, setDiscountType] = useState(''); // 'percentage' or 'fixed'
  const [discountValue, setDiscountValue] = useState(0);
  // Manual adjustments (admin only)
  const [adjustments, setAdjustments] = useState([]); // [{ description, amount_ht, show_on_pdf }]
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill delivery address when client info changes
  useEffect(() => {
    setServices(prev => ({ 
      ...prev, 
      delivery_address: clientInfo.address || prev.delivery_address,
      delivery_city: clientInfo.city || prev.delivery_city,
      delivery_postal_code: clientInfo.postal_code || prev.delivery_postal_code
    }));
  }, [clientInfo.address, clientInfo.city, clientInfo.postal_code]);

  // Load quote data if duplicating
  useEffect(() => {
    if (duplicateFromId && materials.length > 0) {
      loadQuoteForDuplication(duplicateFromId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateFromId, materials]);

  const loadQuoteForDuplication = async (quoteId) => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${quoteId}`);
      const quote = response.data;
      
      // Load client info
      setClientInfo(quote.client_info);
      
      // Find and set material
      const material = materials.find(m => m.id === quote.material_id);
      if (material) {
        setSelectedMaterial(material);
        setSelectedThickness(quote.thickness);
      }
      
      // Load plans (convert from old cm format if needed)
      if (quote.plans && quote.plans.length > 0) {
        setPlans(quote.plans.map((p, idx) => ({
          id: Date.now() + idx,
          designation: p.designation || 'Plan de travail',
          length_mm: p.length_mm || (p.length_cm ? p.length_cm * 10 : 0),
          width_mm: p.width_mm || (p.width_cm ? p.width_cm * 10 : 0)
        })));
      }
      
      // Load finishings
      if (quote.finishings && quote.finishings.length > 0) {
        setSelectedFinishings(quote.finishings.map(f => ({
          id: f.finishing_id,
          quantity: f.quantity
        })));
      }
      
      // Load edges
      if (quote.edges && quote.edges.length > 0) {
        setSelectedEdges(quote.edges.map(e => ({
          edge_type_id: e.edge_type_id,
          length_ml: e.length_ml
        })));
      }
      
      // Load services
      if (quote.services) {
        setServices({
          installation_package: quote.services.installation_package || false,
          delivery_address: quote.services.delivery_address || quote.client_info.address || '',
          delivery_city: quote.services.delivery_city || quote.client_info.city || '',
          delivery_postal_code: quote.services.delivery_postal_code || quote.client_info.postal_code || ''
        });
        if (quote.services.installation_package_price) {
          setInstallationPrice({
            price: quote.services.installation_package_price,
            zone: quote.services.installation_zone,
            is_nous_consulter: false
          });
        }
      }
      
      // Load TVA rate and discount
      setTvaRate(quote.tva_rate || 20);
      setDiscountType(quote.discount_type || '');
      setDiscountValue(quote.discount_value || 0);
      // Load adjustments (admin only — pro view won't have them anyway)
      if (Array.isArray(quote.adjustments) && quote.adjustments.length > 0) {
        setAdjustments(quote.adjustments.map(a => {
          const amt = Number(a.amount_ht) || 0;
          return {
            description: a.description || '',
            amount_ht: amt,
            sign: amt < 0 ? 'subtract' : 'add',
            show_on_pdf: a.show_on_pdf !== false,
          };
        }));
      }
      
      // Load notes
      setNotes(quote.notes || '');
      
      toast.info('Devis chargé pour modification. Un nouveau numéro sera attribué.');
    } catch (error) {
      console.error('Error loading quote:', error);
      toast.error('Erreur lors du chargement du devis');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [materialsRes, categoriesRes, finishingsRes, edgesRes, settingsRes, rulesRes] = await Promise.all([
        axios.get(`${API_URL}/materials`),
        axios.get(`${API_URL}/categories`),
        axios.get(`${API_URL}/finishings`),
        axios.get(`${API_URL}/edges`),
        axios.get(`${API_URL}/settings/services`),
        axios.get(`${API_URL}/installation-rules`)
      ]);
      setMaterials(materialsRes.data);
      setCategories(categoriesRes.data);
      setFinishings(finishingsRes.data);
      setEdges(edgesRes.data);
      setServiceSettings(settingsRes.data);
      setInstallationRules(rulesRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate installation price (includes floor surcharge logic)
  const recalcInstallationPrice = async () => {
    if (!services.installation_package) return;
    const addr = services.delivery_address || clientInfo.address;
    const city = services.delivery_city || clientInfo.city;
    const postal = services.delivery_postal_code || clientInfo.postal_code;
    if (!addr || !city) return;
    try {
      const response = await axios.post(`${API_URL}/calculate-installation-price`, {
        address: addr,
        city: city,
        postal_code: postal,
        has_floor: clientInfo.has_floor,
        floor_number: clientInfo.floor_number,
        has_elevator: clientInfo.has_elevator
      });
      setInstallationPrice(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  // Auto-recalculate when floor info or package toggle changes
  useEffect(() => {
    if (services.installation_package) {
      recalcInstallationPrice();
    } else {
      setInstallationPrice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo.has_floor, clientInfo.floor_number, clientInfo.has_elevator, services.installation_package]);

  // Address autocomplete using OpenStreetMap Nominatim
  const [addressSelected, setAddressSelected] = useState(false);
  
  const searchAddress = async (query) => {
    if (query.length < 3 || addressSelected) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    setSearchingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=fr&limit=5`,
        {
          headers: {
            'Accept-Language': 'fr',
            'User-Agent': 'Granipal-Devis/1.0'
          }
        }
      );
      const data = await response.json();
      
      const suggestions = data.map(item => ({
        display: item.display_name,
        address: item.address?.road || item.address?.pedestrian || item.address?.neighbourhood || '',
        house_number: item.address?.house_number || '',
        city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || '',
        postal_code: item.address?.postcode || '',
        full_address: item.address?.house_number 
          ? `${item.address.house_number} ${item.address.road || ''}`.trim()
          : item.address?.road || item.display_name.split(',')[0]
      }));
      
      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (error) {
      console.error('Address search error:', error);
    } finally {
      setSearchingAddress(false);
    }
  };

  // Debounce address search
  useEffect(() => {
    // Reset addressSelected when user starts typing again manually
    if (addressSelected && clientInfo.address.length < 3) {
      setAddressSelected(false);
    }
    
    const timeoutId = setTimeout(() => {
      if (clientInfo.address && clientInfo.address.length >= 3 && !addressSelected) {
        searchAddress(clientInfo.address + ' France');
      }
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo.address, addressSelected]);

  const selectAddress = (suggestion) => {
    setAddressSelected(true); // Mark as selected to prevent re-search
    setShowSuggestions(false);
    setAddressSuggestions([]);
    setClientInfo({
      ...clientInfo,
      address: suggestion.full_address,
      city: suggestion.city,
      postal_code: suggestion.postal_code
    });
  };
  
  // Reset selection flag when user manually clears and re-types
  const handleAddressChange = (e) => {
    const newValue = e.target.value;
    setClientInfo({ ...clientInfo, address: newValue });
    // If user is typing different content, allow search again
    if (addressSelected) {
      setAddressSelected(false);
    }
  };

  // Get available thicknesses for selected material
  const availableThicknesses = selectedMaterial?.thickness_options?.filter(t => t.available) || [];
  
  // Get price for selected thickness
  const getSelectedThicknessPrice = () => {
    if (!selectedMaterial || !selectedThickness) return { price_per_m2: 0, price_per_ml: 0 };
    const option = availableThicknesses.find(t => t.thickness === selectedThickness);
    return option || { price_per_m2: selectedMaterial.price_per_m2 || 0, price_per_ml: 0 };
  };

  // Calculations (now in mm)
  const calculateSurface = (length_mm, width_mm) => {
    if (!length_mm || !width_mm) return 0;
    return (parseFloat(length_mm) * parseFloat(width_mm)) / 1000000; // mm² to m²
  };

  const totalSurface = plans.reduce((sum, plan) => {
    return sum + calculateSurface(plan.length_mm, plan.width_mm);
  }, 0);

  // Width surcharge: +30% if BOTH dimensions > 700mm (NOT for Granit)
  const hasWidthSurcharge = selectedMaterial && 
    selectedMaterial.category?.toUpperCase() !== 'GRANIT' &&
    plans.some(p => parseFloat(p.length_mm) > 700 && parseFloat(p.width_mm) > 700);

  const thicknessPrice = getSelectedThicknessPrice();
  
  const widthSurchargeAmount = hasWidthSurcharge ? plans.reduce((sum, plan) => {
    // Apply surcharge only to plans where BOTH dimensions > 700mm
    if (parseFloat(plan.length_mm) > 700 && parseFloat(plan.width_mm) > 700) {
      const surface = calculateSurface(plan.length_mm, plan.width_mm);
      return sum + (surface * thicknessPrice.price_per_m2 * 0.30);
    }
    return sum;
  }, 0) : 0;

  const materialTotal = selectedMaterial && selectedThickness
    ? (totalSurface * thicknessPrice.price_per_m2) + widthSurchargeAmount 
    : 0;

  // Détermine le type de matière (granit ou ceramique) pour appliquer les bons tarifs
  const materialType = selectedMaterial?.material_type || 'granit';
  
  // Helpers pour récupérer le bon prix selon le type de matière
  const getFinishingPrice = (f) => {
    if (!f) return 0;
    if (materialType === 'ceramique') return f.price_ceramique || f.price || 0;
    return f.price_granit || f.price || 0;
  };
  const getEdgePrice = (e) => {
    if (!e) return 0;
    if (materialType === 'ceramique') return e.price_ceramique_per_ml || e.price_per_ml || 0;
    return e.price_granit_per_ml || e.price_per_ml || 0;
  };

  const finishingsTotal = selectedFinishings.reduce((sum, sf) => {
    const finishing = finishings.find(f => f.id === sf.id);
    return sum + (getFinishingPrice(finishing) * sf.quantity);
  }, 0);

  const edgesTotal = selectedEdges.reduce((sum, se) => {
    const edge = edges.find(e => e.id === se.edge_type_id);
    return sum + (getEdgePrice(edge) * parseFloat(se.length_ml || 0));
  }, 0);

  // Services total — use manual forfait (Admin & Pro override) when address unknown, else use auto-detected price
  const useManualForfaitLive = services.installation_package && installationAddressUnknown;
  const servicesTotal = useManualForfaitLive
    ? manualForfaitChoice
    : (services.installation_package && installationPrice && !installationPrice.is_nous_consulter
        ? installationPrice.price || 0
        : 0);

  // Sum of manual adjustments (admin only)
  const adjustmentsTotal = user?.role === 'admin'
    ? adjustments.reduce((sum, a) => sum + (Number(a.amount_ht) || 0), 0)
    : 0;

  // === Calculation order (per spec) ===
  // 1. Initial subtotal = materials + finishings + edges + services
  // 2. + Adjustments (admin) → New subtotal
  // 3. − Discount (applies to New subtotal)
  // 4. + TVA → TTC
  const initialSubtotal = materialTotal + finishingsTotal + edgesTotal + servicesTotal;
  const subtotalWithAdjustments = initialSubtotal + adjustmentsTotal;

  const discountAmount = discountType === 'percentage'
    ? subtotalWithAdjustments * (discountValue / 100)
    : discountType === 'fixed' ? Math.min(discountValue, subtotalWithAdjustments) : 0;

  const subtotalHT = subtotalWithAdjustments - discountAmount;
  const tvaAmount = subtotalHT * (tvaRate / 100);
  const totalTTC = subtotalHT + tvaAmount;

  // Handlers
  const addPlan = () => {
    setPlans([...plans, { id: Date.now(), designation: 'Plan de travail', length_mm: 0, width_mm: 0 }]);
  };

  const removePlan = (id) => {
    if (plans.length > 1) {
      setPlans(plans.filter(p => p.id !== id));
    }
  };

  const updatePlan = (id, field, value) => {
    setPlans(plans.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const toggleFinishing = (finishingId) => {
    const exists = selectedFinishings.find(sf => sf.id === finishingId);
    if (exists) {
      setSelectedFinishings(selectedFinishings.filter(sf => sf.id !== finishingId));
    } else {
      setSelectedFinishings([...selectedFinishings, { id: finishingId, quantity: 1 }]);
    }
  };

  const updateFinishingQuantity = (finishingId, quantity) => {
    setSelectedFinishings(selectedFinishings.map(sf => 
      sf.id === finishingId ? { ...sf, quantity: parseInt(quantity) || 1 } : sf
    ));
  };

  const toggleEdge = (edgeId) => {
    const exists = selectedEdges.find(se => se.edge_type_id === edgeId);
    if (exists) {
      setSelectedEdges(selectedEdges.filter(se => se.edge_type_id !== edgeId));
    } else {
      setSelectedEdges([...selectedEdges, { edge_type_id: edgeId, length_ml: 0 }]);
    }
  };

  const updateEdgeLength = (edgeId, length) => {
    setSelectedEdges(selectedEdges.map(se => 
      se.edge_type_id === edgeId ? { ...se, length_ml: length } : se
    ));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return clientInfo.name && clientInfo.phone && clientInfo.email && 
               clientInfo.address && clientInfo.city && clientInfo.postal_code;
      case 2:
        return selectedMaterial && selectedThickness;
      case 3:
        return plans.every(p => p.length_mm > 0 && p.width_mm > 0);
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Determine installation pricing source: auto-detected from address, OR manual forfait override (Admin & Pro when address unknown)
      const useManualForfait = services.installation_package && installationAddressUnknown;
      const finalInstallPrice = useManualForfait ? manualForfaitChoice : (installationPrice?.price || 0);
      const finalInstallZone = useManualForfait ? `Forfait manuel ${manualForfaitChoice}€` : (installationPrice?.zone || '');

      const quoteData = {
        client_info: clientInfo,
        material_id: selectedMaterial.id,
        thickness: selectedThickness,
        plans: plans.map(p => ({
          designation: p.designation === 'Autre' && p.custom_designation 
            ? p.custom_designation 
            : p.designation,
          length_mm: parseFloat(p.length_mm),
          width_mm: parseFloat(p.width_mm)
        })),
        finishing_ids: selectedFinishings,
        edge_selections: selectedEdges.map(se => ({
          edge_type_id: se.edge_type_id,
          length_ml: parseFloat(se.length_ml) || 0
        })),
        services: {
          installation_package: services.installation_package,
          installation_package_price: finalInstallPrice,
          installation_zone: finalInstallZone,
          delivery_address: services.delivery_address,
          delivery_city: services.delivery_city,
          delivery_postal_code: services.delivery_postal_code,
          // Legacy fields for backward compatibility
          gabarit: services.installation_package,
          gabarit_price: useManualForfait ? 0 : (installationPrice?.gabarit_price || 0),
          delivery: services.installation_package,
          delivery_price: 0,
          installation: services.installation_package,
          installation_price: useManualForfait ? finalInstallPrice : (installationPrice?.installation_price || 0)
        },
        tva_rate: tvaRate,
        discount_type: discountType,
        discount_value: discountValue,
        // Admin-only: manual adjustments. Filter empty rows and strip UI-only fields.
        adjustments: (user?.role === 'admin' ? adjustments : [])
          .filter(a => (a.description?.trim() || a.amount_ht !== 0))
          .map(a => ({
            description: a.description || '',
            amount_ht: Number(a.amount_ht) || 0,
            show_on_pdf: a.show_on_pdf !== false,
          })),
        notes
      };

      const response = await axios.post(`${API_URL}/quotes`, quoteData);
      toast.success(`Devis ${response.data.quote_number} créé avec succès !`);
      navigate(`/dashboard/devis/${response.data.id}`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      // Parse error to show user-friendly message
      let errorMsg = 'Erreur lors de la création du devis';
      if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        const fieldErrors = errorDetail.map(e => {
          const field = e.loc?.join(' > ') || 'champ';
          const msg = e.msg || 'invalide';
          // Translate common field names
          const translations = {
            'client_info > name': 'Nom client',
            'client_info > email': 'Email client',
            'client_info > phone': 'Téléphone client',
            'client_info > address': 'Adresse chantier',
            'client_info > city': 'Ville',
            'client_info > postal_code': 'Code postal',
            'material_id': 'Matière',
            'thickness': 'Épaisseur',
            'plans': 'Dimensions'
          };
          const fieldName = translations[field] || field;
          return `${fieldName} manquant ou invalide`;
        });
        errorMsg = fieldErrors[0] || errorMsg;
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 7 && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="configurator">
      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/dashboard">
            <img src={LOGO_URL} alt="Granipal" className="h-10 w-auto" />
          </Link>
          <Link to="/dashboard" className="text-white/50 hover:text-white text-sm transition-colors">
            Retour au dashboard
          </Link>
        </div>
      </header>

      {/* Progress */}
      <div className="bg-[#1C1C1C] border-b border-white/5 px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => isCompleted && setCurrentStep(step.id)}
                    className={`flex flex-col items-center gap-2 ${isCompleted ? 'cursor-pointer' : 'cursor-default'}`}
                    disabled={!isCompleted && !isActive}
                  >
                    <div className={`step-indicator ${isActive ? 'active' : isCompleted ? 'completed' : 'pending'}`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <span className={`text-xs uppercase tracking-wider hidden sm:block ${
                      isActive ? 'text-[#D4AF37]' : isCompleted ? 'text-white' : 'text-white/30'
                    }`}>
                      {step.title}
                    </span>
                  </button>
                  
                  {index < steps.length - 1 && (
                    <div className={`w-8 sm:w-16 lg:w-24 h-[1px] mx-2 ${
                      currentStep > step.id ? 'bg-[#D4AF37]' : 'bg-white/10'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex">
        {/* Form Section */}
        <div className="flex-1 lg:w-3/5 p-6 lg:p-12 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-2xl"
            >
              {/* Step 1: Client */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Informations client</h2>
                    <p className="text-white/50">Renseignez les coordonnées du client final</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Nom complet *</label>
                      <Input
                        value={clientInfo.name}
                        onChange={(e) => setClientInfo({...clientInfo, name: e.target.value})}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                        placeholder="Nom du client"
                        data-testid="client-name"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Téléphone *</label>
                      <Input
                        value={clientInfo.phone}
                        onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                        placeholder="06 00 00 00 00"
                        data-testid="client-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Email *</label>
                    <Input
                      type="email"
                      value={clientInfo.email}
                      onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
                      className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                      placeholder="client@email.com"
                      data-testid="client-email"
                    />
                  </div>

                  <div className="relative">
                    <label className="text-white/60 text-sm mb-2 block">Adresse chantier *</label>
                    <div className="relative">
                      <Input
                        value={clientInfo.address}
                        onChange={handleAddressChange}
                        onFocus={() => {
                          if (addressSuggestions.length > 0 && !addressSelected) {
                            setShowSuggestions(true);
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click on suggestion
                          setTimeout(() => setShowSuggestions(false), 200);
                        }}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                        placeholder="Commencez à taper une adresse..."
                        data-testid="client-address"
                      />
                      {searchingAddress && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                    
                    {/* Address suggestions dropdown */}
                    {showSuggestions && addressSuggestions.length > 0 && !addressSelected && (
                      <div className="absolute z-50 w-full mt-1 bg-[#1C1C1C] border border-white/20 shadow-lg max-h-60 overflow-y-auto">
                        {addressSuggestions.map((suggestion, index) => (
                          <button
                            key={`${suggestion.full_address}-${suggestion.postal_code}-${index}`}
                            type="button"
                            className="w-full px-4 py-3 text-left hover:bg-[#D4AF37]/10 transition-colors border-b border-white/5 last:border-0"
                            onClick={() => selectAddress(suggestion)}
                            data-testid={`address-suggestion-${index}`}
                          >
                            <p className="text-white text-sm font-medium truncate">{suggestion.full_address}</p>
                            <p className="text-white/50 text-xs mt-1 truncate">
                              {suggestion.postal_code} {suggestion.city}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Code postal à gauche, Ville à droite */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Code postal *</label>
                      <Input
                        value={clientInfo.postal_code}
                        onChange={(e) => setClientInfo({...clientInfo, postal_code: e.target.value})}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                        placeholder="00000"
                        data-testid="client-postal"
                      />
                    </div>
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Ville *</label>
                      <Input
                        value={clientInfo.city}
                        onChange={(e) => setClientInfo({...clientInfo, city: e.target.value})}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                        placeholder="Ville"
                        data-testid="client-city"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10 space-y-4">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={clientInfo.has_floor}
                        onCheckedChange={(checked) => setClientInfo({...clientInfo, has_floor: checked})}
                        className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                        data-testid="has-floor"
                      />
                      <label className="text-white text-sm">Logement en étage</label>
                    </div>

                    {clientInfo.has_floor && (
                      <div className="grid sm:grid-cols-2 gap-4 pl-6">
                        <div>
                          <label className="text-white/60 text-sm mb-2 block">Numéro d'étage</label>
                          <Input
                            type="number"
                            min="0"
                            value={clientInfo.floor_number}
                            onChange={(e) => setClientInfo({...clientInfo, floor_number: parseInt(e.target.value) || 0})}
                            className="bg-[#1C1C1C] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                            data-testid="floor-number"
                          />
                        </div>
                        <div className="flex items-end">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={clientInfo.has_elevator}
                              onCheckedChange={(checked) => setClientInfo({...clientInfo, has_elevator: checked})}
                              className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                              data-testid="has-elevator"
                            />
                            <label className="text-white text-sm">Ascenseur disponible</label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Adresse chantier inconnue (Admin + Pro) */}
                  <div className="pt-4 border-t border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={installationAddressUnknown}
                        onCheckedChange={(checked) => setInstallationAddressUnknown(!!checked)}
                        className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
                        data-testid="install-address-unknown"
                      />
                      <div>
                        <p className="text-white font-medium text-sm">Adresse de chantier inconnue pour le moment</p>
                        <p className="text-white/40 text-xs mt-0.5">
                          Si vous n’avez pas encore l’adresse exacte du chantier, vous pourrez sélectionner manuellement le forfait pose (1 100€ ou 950€) à l’étape « Services ».
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 2: Material Selection - 3 distinct steps */}
              {currentStep === 2 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Sélection matière</h2>
                    <p className="text-white/50">Choisissez la catégorie, le coloris, puis l'épaisseur</p>
                  </div>

                  {/* Step 2.1: Category Selection */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-black font-bold text-sm">1</div>
                      <label className="text-white font-medium text-lg">Catégorie</label>
                    </div>
                    <div className="flex flex-wrap gap-3 pl-11">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            setSelectedCategory(category.name);
                            setSelectedMaterial(null);
                            setSelectedThickness('');
                          }}
                          className={`px-5 py-3 border transition-all ${
                            selectedCategory === category.name
                              ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                              : 'bg-[#1C1C1C] border-white/20 text-white hover:border-white/40'
                          }`}
                          data-testid={`category-${category.id}`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Separator */}
                  {selectedCategory && (
                    <div className="border-t border-white/10"></div>
                  )}

                  {/* Step 2.2: Color/Material Selection — LISTE VERTICALE avec compactage */}
                  {selectedCategory && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-black font-bold text-sm">2</div>
                        <label className="text-white font-medium text-lg">Coloris</label>
                      </div>
                      
                      {/* Si coloris sélectionné : affiché en compact, cliquable pour rouvrir la liste */}
                      {selectedMaterial ? (
                        <div className="pl-11">
                          <button
                            onClick={() => { setSelectedMaterial(null); setSelectedThickness(''); }}
                            className="w-full px-4 py-3 bg-[#D4AF37]/15 border border-[#D4AF37] flex items-center justify-between hover:bg-[#D4AF37]/25 transition-colors text-left"
                            data-testid="selected-material-compact"
                            title="Cliquer pour changer de coloris"
                          >
                            <div>
                              <p className="text-white font-medium">{selectedMaterial.name}</p>
                              {selectedMaterial.finition && (
                                <p className="text-white/50 text-xs mt-0.5">Finition : {selectedMaterial.finition}</p>
                              )}
                            </div>
                            <span className="text-[#D4AF37] text-xs font-semibold">✓ Sélectionné</span>
                          </button>
                        </div>
                      ) : (
                        /* Liste verticale des coloris */
                        <div className="pl-11 max-h-[420px] overflow-y-auto pr-2 space-y-1.5 border border-white/10 rounded-md bg-[#0A0A0A]/40 p-2">
                          {materials
                            .filter(m => m.category === selectedCategory)
                            .map((material) => (
                              <button
                                key={material.id}
                                onClick={() => { setSelectedMaterial(material); setSelectedThickness(''); }}
                                className="w-full px-4 py-2.5 border bg-[#1C1C1C] border-white/10 hover:border-[#D4AF37]/50 text-left transition-all flex items-center justify-between"
                                data-testid={`material-${material.id}`}
                              >
                                <div>
                                  <p className="text-white font-medium text-sm">{material.name}</p>
                                  {material.finition && (
                                    <p className="text-white/40 text-xs mt-0.5">Finition : {material.finition}</p>
                                  )}
                                </div>
                                <span className="text-white/50 text-xs whitespace-nowrap ml-3">
                                  {material.thickness_options?.length || 0} ép.
                                </span>
                              </button>
                            ))}
                          {materials.filter(m => m.category === selectedCategory).length === 0 && (
                            <p className="text-white/40 text-center py-4">
                              Aucun coloris disponible dans cette catégorie
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2.3: Épaisseur — ALIGNÉE À GAUCHE comme étapes 1 et 2 */}
                  {selectedMaterial && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-black font-bold text-sm">3</div>
                        <label className="text-white font-medium text-lg">Épaisseur</label>
                        {!selectedThickness && (
                          <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/50 text-red-400 text-[10px] font-semibold animate-pulse">
                            OBLIGATOIRE
                          </span>
                        )}
                      </div>
                      {availableThicknesses.length > 0 ? (
                        <div className="pl-11 flex flex-wrap gap-3">
                          {availableThicknesses.map((option) => (
                            <button
                              key={option.thickness}
                              onClick={() => setSelectedThickness(option.thickness)}
                              className={`px-5 py-3 border-2 transition-all ${
                                selectedThickness === option.thickness
                                  ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                                  : 'bg-transparent border-white/20 text-white hover:border-[#D4AF37]/50'
                              }`}
                              data-testid={`thickness-${option.thickness.replace(/[,\s]/g, '')}`}
                            >
                              <div className="text-center">
                                <p className="font-bold">{option.thickness}</p>
                                <p className="text-xs mt-0.5 opacity-70">
                                  {option.price_pro_m2 || option.price_per_m2} €/m²
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="pl-11">
                          <div className="p-3 bg-yellow-500/10 border border-yellow-500/30">
                            <p className="text-yellow-500 text-sm">
                              Aucune épaisseur configurée pour cette matière.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Dimensions - Now in mm */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Dimensions</h2>
                    <p className="text-white/50">Définissez les dimensions de chaque plan en millimètres</p>
                  </div>

                  <div className="space-y-4">
                    {plans.map((plan, index) => (
                      <div key={plan.id} className="p-4 bg-[#1C1C1C] border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-white font-medium">Plan {index + 1}</span>
                          {plans.length > 1 && (
                            <button
                              onClick={() => removePlan(plan.id)}
                              className="text-red-400 hover:text-red-300 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Designation dropdown */}
                        <div className="mb-4">
                          <label className="text-white/60 text-sm mb-2 block">Désignation</label>
                          <Select 
                            value={plan.designation} 
                            onValueChange={(v) => updatePlan(plan.id, 'designation', v)}
                          >
                            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1C1C1C] border-white/10">
                              {planDesignations.map((des) => (
                                <SelectItem key={des} value={des} className="text-white hover:bg-white/10">
                                  {des}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {/* Custom designation field for "Autre" */}
                          {plan.designation === 'Autre' && (
                            <Input
                              value={plan.custom_designation || ''}
                              onChange={(e) => updatePlan(plan.id, 'custom_designation', e.target.value)}
                              className="mt-2 bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                              placeholder="Préciser la désignation (tablette, habillage, banc...)"
                              data-testid={`plan-${index}-custom-designation`}
                            />
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-white/60 text-sm mb-2 block">Longueur (mm) *</label>
                            <Input
                              type="number"
                              min="0"
                              value={plan.length_mm || ''}
                              onChange={(e) => updatePlan(plan.id, 'length_mm', e.target.value)}
                              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                              placeholder="0"
                              data-testid={`plan-${index}-length`}
                            />
                          </div>
                          <div>
                            <label className="text-white/60 text-sm mb-2 block">Largeur (mm) *</label>
                            <Input
                              type="number"
                              min="0"
                              value={plan.width_mm || ''}
                              onChange={(e) => updatePlan(plan.id, 'width_mm', e.target.value)}
                              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                              placeholder="0"
                              data-testid={`plan-${index}-width`}
                            />
                          </div>
                        </div>
                        <div className="mt-3 flex justify-between text-sm">
                          <span className="text-white/50">Surface:</span>
                          <span className="text-white">
                            {calculateSurface(plan.length_mm, plan.width_mm).toFixed(2)} m²
                          </span>
                        </div>
                        {parseFloat(plan.width_mm) > 700 && selectedMaterial?.category?.toUpperCase() !== 'GRANIT' && (
                          <div className="mt-2 text-yellow-500 text-xs flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Plus-value largeur {'>'}700mm applicable
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={addPlan}
                    className="btn-secondary flex items-center gap-2 w-full justify-center"
                    data-testid="add-plan-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un plan
                  </button>

                  {totalSurface > 0 && (
                    <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                      <div className="flex justify-between">
                        <span className="text-white">Surface totale:</span>
                        <span className="text-[#D4AF37] font-bold">{totalSurface.toFixed(2)} m²</span>
                      </div>
                      {totalSurface < 2 && (
                        <p className="text-yellow-500 text-xs mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Surface {'<'} 2m² – tarif spécifique, nous consulter
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Finishings */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Façonnage</h2>
                    <p className="text-white/50">Sélectionnez les options de façonnage</p>
                  </div>

                  <div className="space-y-3">
                    {finishings.map((finishing) => {
                      const isSelected = selectedFinishings.find(sf => sf.id === finishing.id);
                      return (
                        <div 
                          key={finishing.id}
                          className={`p-4 border transition-all ${
                            isSelected 
                              ? 'bg-[#D4AF37]/10 border-[#D4AF37]' 
                              : 'bg-[#1C1C1C] border-white/10'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={!!isSelected}
                                onCheckedChange={() => toggleFinishing(finishing.id)}
                                className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                                data-testid={`finishing-${finishing.id}`}
                              />
                              <div>
                                <p className="text-white font-medium">{finishing.name}</p>
                                <p className="text-white/50 text-sm">{getFinishingPrice(finishing)} € / unité</p>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="flex items-center gap-2">
                                <span className="text-white/50 text-sm">Qté:</span>
                                <Input
                                  type="number"
                                  min="1"
                                  value={isSelected.quantity}
                                  onChange={(e) => updateFinishingQuantity(finishing.id, e.target.value)}
                                  className="w-16 bg-[#0A0A0A] border-white/10 text-white rounded-none text-center"
                                  data-testid={`finishing-qty-${finishing.id}`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedFinishings.length === 0 && (
                    <p className="text-white/40 text-center py-4">
                      Aucun façonnage sélectionné (optionnel)
                    </p>
                  )}
                </div>
              )}

              {/* Step 5: Edges */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Chants visibles</h2>
                    <p className="text-white/50">Définissez les chants et leurs longueurs</p>
                  </div>

                  {/* Warning about ml */}
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <p className="text-amber-400 text-sm">
                      Les chants sont calculés en <span className="font-semibold text-amber-500">mètres linéaires (ml)</span>.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {edges.map((edge) => {
                      const isSelected = selectedEdges.find(se => se.edge_type_id === edge.id);
                      return (
                        <div 
                          key={edge.id}
                          className={`p-4 border transition-all ${
                            isSelected 
                              ? 'bg-[#D4AF37]/10 border-[#D4AF37]' 
                              : 'bg-[#1C1C1C] border-white/10'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={!!isSelected}
                                onCheckedChange={() => toggleEdge(edge.id)}
                                className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                                data-testid={`edge-${edge.id}`}
                              />
                              <div>
                                <p className="text-white font-medium">{edge.name}</p>
                                <p className="text-white/50 text-sm">
                                  {getEdgePrice(edge)} € / <span className="text-red-400 font-semibold">ml</span>
                                </p>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="flex items-center gap-2">
                                <span className="text-red-400 text-sm font-semibold">Longueur (ml):</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={isSelected.length_ml || ''}
                                  onChange={(e) => updateEdgeLength(edge.id, e.target.value)}
                                  className="w-24 bg-[#0A0A0A] border-white/10 text-white rounded-none text-center"
                                  placeholder="0"
                                  data-testid={`edge-length-${edge.id}`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedEdges.length === 0 && (
                    <p className="text-white/40 text-center py-4">
                      Aucun chant sélectionné (optionnel)
                    </p>
                  )}
                </div>
              )}

              {/* Step 6: Services ONLY */}
              {currentStep === 6 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Forfait pose complète</h2>
                    <p className="text-white/50">Inclut la prise de gabarit, la livraison et la pose</p>
                  </div>

                  <div className={`p-5 border-2 transition-all ${
                    services.installation_package ? 'bg-[#D4AF37]/10 border-[#D4AF37]' : 'bg-[#1C1C1C] border-white/10'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={services.installation_package}
                          onCheckedChange={(checked) => {
                            setServices({...services, installation_package: checked});
                            // Price recalc happens via useEffect on services.installation_package
                          }}
                          className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] mt-1"
                          data-testid="service-package"
                        />
                        <div>
                          <p className="text-white font-semibold text-lg">Oui, je souhaite le forfait complet</p>
                          <p className="text-white/50 text-sm mt-1">Prise de gabarit + Livraison + Pose</p>
                        </div>
                      </div>
                      {installationAddressUnknown && services.installation_package && (
                        <div className="text-right" data-testid="manual-forfait-summary">
                          <span className="text-[#D4AF37] font-bold text-xl" data-testid="manual-forfait-price">{manualForfaitChoice.toFixed(2)} € HT</span>
                          <p className="text-white/40 text-sm">Forfait manuel</p>
                        </div>
                      )}
                      {!installationAddressUnknown && installationPrice && !installationPrice.is_nous_consulter && (
                        <div className="text-right">
                          <span className="text-[#D4AF37] font-bold text-xl" data-testid="installation-total-price">{installationPrice.price?.toFixed(2)} € HT</span>
                          <p className="text-white/40 text-sm">{installationPrice.zone}</p>
                          {installationPrice.floor_surcharge > 0 && (
                            <p className="text-[#D4AF37]/80 text-xs mt-1" data-testid="floor-surcharge-display">
                              + {installationPrice.floor_surcharge.toFixed(2)} € (étage {installationPrice.floor_number})
                            </p>
                          )}
                        </div>
                      )}
                      {!installationAddressUnknown && installationPrice && installationPrice.is_nous_consulter && (
                        <div className="text-right">
                          <span className="text-yellow-500 font-bold" data-testid="installation-nous-consulter">Nous consulter</span>
                          {installationPrice.is_nous_consulter_floor ? (
                            <p className="text-yellow-500/80 text-xs mt-1">
                              Étage {installationPrice.floor_number} — devis manuel requis
                            </p>
                          ) : (
                            <p className="text-white/40 text-sm">Distance: {installationPrice.distance_km} km</p>
                          )}
                        </div>
                      )}
                    </div>

                    {services.installation_package && !installationAddressUnknown && (
                      <div className="mt-5 pt-4 border-t border-white/10">
                        <label className="text-white/60 text-sm mb-3 block">Lieu de pose (modifiable si différent)</label>
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div className="sm:col-span-2">
                            <Input
                              value={services.delivery_address}
                              onChange={async (e) => {
                                const newAddress = e.target.value;
                                setServices({...services, delivery_address: newAddress});
                              }}
                              onBlur={async () => {
                                // Auto-recalculate on blur
                                if (services.delivery_address && services.delivery_city) {
                                  await recalcInstallationPrice();
                                }
                              }}
                              className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                              placeholder="Adresse"
                              data-testid="delivery-address"
                            />
                          </div>
                          <Input
                            value={services.delivery_postal_code}
                            onChange={(e) => setServices({...services, delivery_postal_code: e.target.value})}
                            onBlur={async () => {
                              if (services.delivery_postal_code && services.delivery_city) {
                                await recalcInstallationPrice();
                              }
                            }}
                            className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                            placeholder="Code postal"
                            data-testid="delivery-postal"
                          />
                        </div>
                        <Input
                          value={services.delivery_city}
                          onChange={(e) => setServices({...services, delivery_city: e.target.value})}
                          onBlur={async () => {
                            if (services.delivery_city) {
                              await recalcInstallationPrice();
                            }
                          }}
                          className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37] mt-3"
                          placeholder="Ville"
                          data-testid="delivery-city"
                        />
                      </div>
                    )}

                    {/* Manual forfait selection — visible when address is unknown (Admin AND Pro) */}
                    {services.installation_package && installationAddressUnknown && (
                      <div className="mt-5 pt-4 border-t border-white/10" data-testid="manual-forfait-section">
                        <div className="mb-3">
                          <p className="text-white font-medium text-sm">Sélection manuelle du forfait pose</p>
                          <p className="text-white/50 text-xs mt-1">
                            L’adresse du chantier n’étant pas encore connue, choisissez le forfait adapté à la zone estimée :
                          </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setManualForfaitChoice(1100)}
                            className={`px-4 py-4 border-2 transition-all text-left ${
                              manualForfaitChoice === 1100
                                ? 'bg-[#D4AF37]/15 border-[#D4AF37]'
                                : 'bg-[#0A0A0A] border-white/10 hover:border-white/30'
                            }`}
                            data-testid="manual-forfait-1100"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`w-3 h-3 rounded-full border-2 ${
                                manualForfaitChoice === 1100 ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                              }`} />
                              <span className="text-white font-semibold">Forfait 1 100 € HT</span>
                            </div>
                            <p className="text-white/50 text-xs">Paris et proche banlieue</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setManualForfaitChoice(950)}
                            className={`px-4 py-4 border-2 transition-all text-left ${
                              manualForfaitChoice === 950
                                ? 'bg-[#D4AF37]/15 border-[#D4AF37]'
                                : 'bg-[#0A0A0A] border-white/10 hover:border-white/30'
                            }`}
                            data-testid="manual-forfait-950"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`w-3 h-3 rounded-full border-2 ${
                                manualForfaitChoice === 950 ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-white/30'
                              }`} />
                              <span className="text-white font-semibold">Forfait 950 € HT</span>
                            </div>
                            <p className="text-white/50 text-xs">Autres zones</p>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 7: TVA & Remise */}
              {currentStep === 7 && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">TVA & Remise</h2>
                    <p className="text-white/50">Sélectionnez le taux de TVA et appliquez une remise si nécessaire</p>
                  </div>

                  {/* TVA Selection */}
                  <div className="space-y-4">
                    <label className="text-white font-medium text-lg">Taux de TVA</label>
                    <div className="flex flex-wrap gap-3">
                      {tvaOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setTvaRate(option.value)}
                          className={`px-6 py-3 border transition-all ${
                            tvaRate === option.value
                              ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                              : 'bg-transparent border-white/20 text-white hover:border-white/40'
                          }`}
                          data-testid={`tva-${option.value}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-white/10"></div>

                  {/* Remise */}
                  <div className="space-y-4">
                    <label className="text-white font-medium text-lg">Remise</label>
                    <p className="text-white/50 text-sm">La remise sera appliquée au total HT avant la TVA</p>
                    <div className="flex flex-wrap items-center gap-4">
                      <Select
                        value={discountType}
                        onValueChange={(value) => {
                          setDiscountType(value);
                          if (!value) setDiscountValue(0);
                        }}
                      >
                        <SelectTrigger className="w-48 bg-[#1C1C1C] border-white/10 text-white">
                          <SelectValue placeholder="Type de remise" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1C] border-white/10">
                          <SelectItem value="none" className="text-white hover:bg-white/10">Aucune remise</SelectItem>
                          <SelectItem value="percentage" className="text-white hover:bg-white/10">Pourcentage (%)</SelectItem>
                          <SelectItem value="fixed" className="text-white hover:bg-white/10">Montant fixe (€)</SelectItem>
                        </SelectContent>
                      </Select>
                      {discountType && discountType !== 'none' && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            step={discountType === 'percentage' ? '1' : '0.01'}
                            max={discountType === 'percentage' ? '100' : undefined}
                            value={discountValue || ''}
                            onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                            className="w-28 bg-[#0A0A0A] border-white/10 text-white rounded-none text-center"
                            placeholder="0"
                            data-testid="discount-value"
                          />
                          <span className="text-white/50 text-lg">{discountType === 'percentage' ? '%' : '€'}</span>
                        </div>
                      )}
                    </div>
                    {discountAmount > 0 && (
                      <p className="text-[#D4AF37] text-sm">
                        Remise appliquée: -{discountAmount.toFixed(2)} €
                      </p>
                    )}
                  </div>

                  {/* ADMIN ONLY: Ajustements manuels (+ / -) */}
                  {user?.role === 'admin' && (
                    <>
                      <div className="border-t border-white/10"></div>
                      <div className="space-y-3" data-testid="adjustments-section">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-white font-medium text-lg">Ajustements manuels</label>
                            <p className="text-white/50 text-sm mt-1">
                              Choisissez « Ajouter » ou « Retirer », puis saisissez un montant HT positif. Visible uniquement côté Granipal.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setAdjustments(prev => [...prev, { description: '', amount_ht: 0, sign: 'add', show_on_pdf: true }])}
                            className="px-4 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#C19F30] transition-colors text-sm whitespace-nowrap"
                            data-testid="add-adjustment-btn"
                          >
                            + Ajouter
                          </button>
                        </div>
                        {adjustments.length === 0 && (
                          <p className="text-white/30 text-sm italic">Aucun ajustement.</p>
                        )}
                        {adjustments.map((adj, i) => {
                          // Derive sign + absolute amount from amount_ht for backward compat with loaded quotes
                          const currentSign = adj.sign || (Number(adj.amount_ht) < 0 ? 'subtract' : 'add');
                          const absAmount = Math.abs(Number(adj.amount_ht) || 0);
                          const updateAdj = (patch) => {
                            const next = [...adjustments];
                            const merged = { ...next[i], ...patch };
                            const sign = merged.sign || currentSign;
                            const abs = Number(merged._absAmount !== undefined ? merged._absAmount : absAmount) || 0;
                            const signed = sign === 'subtract' ? -abs : abs;
                            next[i] = {
                              description: merged.description !== undefined ? merged.description : next[i].description,
                              amount_ht: signed,
                              sign,
                              show_on_pdf: merged.show_on_pdf !== undefined ? merged.show_on_pdf : next[i].show_on_pdf,
                            };
                            setAdjustments(next);
                          };
                          return (
                            <div
                              key={`adj-${i}`}
                              className="p-4 bg-[#1C1C1C] border border-white/10 space-y-3"
                              data-testid={`adjustment-row-${i}`}
                            >
                              <div className="grid sm:grid-cols-[1fr,140px,150px,auto] gap-3 items-center">
                                <Input
                                  type="text"
                                  value={adj.description}
                                  onChange={(e) => updateAdj({ description: e.target.value })}
                                  placeholder="Description / justification"
                                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37]"
                                  data-testid={`adjustment-description-${i}`}
                                />
                                <Select
                                  value={currentSign}
                                  onValueChange={(value) => updateAdj({ sign: value, _absAmount: absAmount })}
                                >
                                  <SelectTrigger
                                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                                    data-testid={`adjustment-sign-${i}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                                    <SelectItem value="add" className="text-white hover:bg-white/10" data-testid={`adjustment-sign-${i}-add`}>
                                      <span className="text-[#D4AF37]">+ Ajouter</span>
                                    </SelectItem>
                                    <SelectItem value="subtract" className="text-white hover:bg-white/10" data-testid={`adjustment-sign-${i}-subtract`}>
                                      <span className="text-orange-400">− Retirer</span>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={absAmount === 0 ? '' : absAmount}
                                    onChange={(e) => {
                                      const raw = parseFloat(e.target.value);
                                      const newAbs = Number.isFinite(raw) && raw >= 0 ? raw : 0;
                                      updateAdj({ _absAmount: newAbs });
                                    }}
                                    placeholder="0.00"
                                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none focus:border-[#D4AF37] text-right"
                                    data-testid={`adjustment-amount-${i}`}
                                  />
                                  <span className="text-white/50 text-sm whitespace-nowrap">€ HT</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setAdjustments(prev => prev.filter((_, j) => j !== i))}
                                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors"
                                  aria-label="Supprimer"
                                  data-testid={`adjustment-remove-${i}`}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <label className="flex items-center gap-2.5 cursor-pointer">
                                <Checkbox
                                  checked={adj.show_on_pdf}
                                  onCheckedChange={(checked) => updateAdj({ show_on_pdf: !!checked })}
                                  className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                                  data-testid={`adjustment-showpdf-${i}`}
                                />
                                <span className="text-white/70 text-sm">Afficher sur le PDF</span>
                                {!adj.show_on_pdf && (
                                  <span className="text-white/40 text-xs italic">— montant inclus mais ligne masquée</span>
                                )}
                              </label>
                            </div>
                          );
                        })}
                        {adjustments.length > 0 && (
                          <p className="text-[#D4AF37] text-sm">
                            Total ajustements : {adjustmentsTotal >= 0 ? '+' : ''}{adjustmentsTotal.toFixed(2)} € HT
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Separator */}
                  <div className="border-t border-white/10"></div>

                  {/* Informations complémentaires (notes) */}
                  <div className="space-y-3">
                    <label className="text-white font-medium text-lg">Informations complémentaires</label>
                    <p className="text-white/50 text-sm">
                      Note libre liée au devis : précision chantier, remarque client, contrainte particulière, délai spécifique…
                    </p>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={5}
                      maxLength={1000}
                      placeholder="Ex : accès chantier par cour intérieure, livraison le mardi uniquement, attention présence d'enfant…"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white text-sm rounded-none focus:border-[#D4AF37] focus:outline-none p-3 resize-y min-h-[120px]"
                      data-testid="quote-notes"
                    />
                    <p className="text-white/30 text-xs text-right">{notes.length} / 1000</p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="btn-secondary flex items-center gap-2 disabled:opacity-30"
              data-testid="prev-step-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Précédent
            </button>

            {currentStep < 7 ? (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="btn-primary flex items-center gap-2 disabled:opacity-30"
                data-testid="next-step-btn"
              >
                Suivant
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary flex items-center gap-2"
                data-testid="submit-quote-btn"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Créer le devis
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Summary Sidebar */}
        <div className="hidden lg:block w-2/5 bg-[#1C1C1C] border-l border-white/5 p-8 overflow-y-auto">
          <h3 className="text-lg font-bold text-white mb-6">Récapitulatif</h3>
          
          <div className="space-y-6">
            {/* Material */}
            {selectedMaterial && (
              <div className="pb-4 border-b border-white/10">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Matière</p>
                <p className="text-white font-medium">{selectedMaterial.name}</p>
                <p className="text-white/50 text-sm">{selectedMaterial.category} {selectedThickness && `- ${selectedThickness}`}</p>
                {selectedThickness && (
                  <p className="text-white/50 text-sm">{thicknessPrice.price_per_m2} €/m²</p>
                )}
              </div>
            )}

            {/* Surface */}
            {totalSurface > 0 && (
              <div className="pb-4 border-b border-white/10">
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">Surface</p>
                <p className="text-white font-medium">{totalSurface.toFixed(2)} m²</p>
                {plans.length > 1 && (
                  <p className="text-white/50 text-sm">{plans.length} plans</p>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Matière</span>
                <span className="text-white">{materialTotal.toFixed(2)} €</span>
              </div>
              {hasWidthSurcharge && (
                <div className="flex justify-between text-yellow-500">
                  <span>dont plus-value largeur</span>
                  <span>+{widthSurchargeAmount.toFixed(2)} €</span>
                </div>
              )}
              {finishingsTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Façonnage</span>
                  <span className="text-white">{finishingsTotal.toFixed(2)} €</span>
                </div>
              )}
              {edgesTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Chants</span>
                  <span className="text-white">{edgesTotal.toFixed(2)} €</span>
                </div>
              )}
              {servicesTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/50">Services</span>
                  <span className="text-white">{servicesTotal.toFixed(2)} €</span>
                </div>
              )}

              <div className="pt-4 border-t border-white/10">
                {/* Sous-total initial (matière + façonnage + chants + services) */}
                <div className="flex justify-between">
                  <span className="text-white/50">Sous-total HT</span>
                  <span className="text-white">{initialSubtotal.toFixed(2)} €</span>
                </div>
                {/* Adjustments line (admin only, only when there are adjustments) */}
                {user?.role === 'admin' && adjustments.length > 0 && adjustmentsTotal !== 0 && (
                  <div className={`flex justify-between mt-1 ${adjustmentsTotal >= 0 ? 'text-[#D4AF37]' : 'text-orange-400'}`}>
                    <span>Ajustements</span>
                    <span>{adjustmentsTotal >= 0 ? '+' : ''}{adjustmentsTotal.toFixed(2)} €</span>
                  </div>
                )}
                {/* Remise — applied AFTER adjustments */}
                {discountAmount > 0 && (
                  <div className="flex justify-between mt-1 text-green-400">
                    <span>Remise {discountType === 'percentage' ? `(${discountValue}%)` : ''}</span>
                    <span>-{discountAmount.toFixed(2)} €</span>
                  </div>
                )}
                {/* Total HT net */}
                <div className="flex justify-between mt-1 pt-2 border-t border-white/5">
                  <span className="text-white/70">Total HT</span>
                  <span className="text-white font-medium">{subtotalHT.toFixed(2)} €</span>
                </div>
                {tvaRate > 0 && (
                  <div className="flex justify-between mt-1">
                    <span className="text-white/50">TVA ({tvaRate}%)</span>
                    <span className="text-white">{tvaAmount.toFixed(2)} €</span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-[#D4AF37]/30">
                <div className="flex justify-between">
                  <span className="text-white font-bold">{tvaRate > 0 ? 'Total TTC' : 'Total HT'}</span>
                  <span className="text-[#D4AF37] text-2xl font-bold">
                    {tvaRate > 0 ? totalTTC.toFixed(2) : subtotalHT.toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>

            {/* Small Surface Warning */}
            {totalSurface > 0 && totalSurface < 2 && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-yellow-500 text-xs flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Surface {'<'} 2m² – tarif spécifique
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
